import { DomainError } from '../errors.js';
import { REVIEW_DUE_DAYS } from './risk.js';
import { RA_AUTHORIZATION_MAX_HOURS } from './verification.js';

/**
 * Risk modülünün şirket ayarları (K7; ADR 0095 §3; README R25): gözden geçirme aralığı ve uyarı
 * penceresi, operasyon yetkisinin en uzun geçerliliği ve okundu onayının gecikme eşiği. Ayarlar
 * kuralı yalnız sıkılaştırır: gözden geçirme en çok 12 ay (ADR 0050 §3), yetki en çok 24 saat
 * (ADR 0089 §4) kesin sınırdır; ayar bunları uzatamaz.
 */
export interface RaSettingsValues {
  /** Yeni RA'nın gözden geçirme günü bu kadar ay sonra; yayım kapısının üst sınırı (1–12). */
  reviewMonths: number;
  /** Gözden geçirmesine bu kadar gün kalan sürüm "due" (uyarı penceresi; ADR 0050 §4). */
  reviewDueDays: number;
  /** Operasyon yetkisinin en uzun geçerliliği, saat (1–24). */
  authorizationMaxHours: number;
  /** Atanmış sürüm bu kadar gün onaylanmazsa okundu onayı gecikmiş sayılır (ofis uyarısı). */
  acknowledgementDueDays: number;
}

export const RA_SETTINGS_DEFAULTS: Readonly<RaSettingsValues> = {
  reviewMonths: 12,
  reviewDueDays: REVIEW_DUE_DAYS,
  authorizationMaxHours: RA_AUTHORIZATION_MAX_HOURS,
  acknowledgementDueDays: 14,
};

export const RA_SETTING_KEYS = [
  'reviewMonths',
  'reviewDueDays',
  'authorizationMaxHours',
  'acknowledgementDueDays',
] as const;

export const RA_SETTING_LIMITS: Readonly<
  Record<keyof RaSettingsValues, { min: number; max: number }>
> = {
  reviewMonths: { min: 1, max: 12 },
  reviewDueDays: { min: 1, max: 180 },
  authorizationMaxHours: { min: 1, max: RA_AUTHORIZATION_MAX_HOURS },
  acknowledgementDueDays: { min: 1, max: 90 },
};

/** Gönderilen ayarları denetler; bilinmeyen alan ya da aralık dışı değer `INVALID_RA_SETTINGS`. */
export function parseRaSettings(input: Record<string, unknown>): Partial<RaSettingsValues> {
  const out: Partial<RaSettingsValues> = {};
  for (const [key, value] of Object.entries(input)) {
    if (!(RA_SETTING_KEYS as readonly string[]).includes(key)) {
      throw new DomainError('INVALID_RA_SETTINGS', key);
    }
    const limit = RA_SETTING_LIMITS[key as keyof RaSettingsValues];
    if (
      typeof value !== 'number' ||
      !Number.isInteger(value) ||
      value < limit.min ||
      value > limit.max
    ) {
      throw new DomainError('INVALID_RA_SETTINGS', key);
    }
    out[key as keyof RaSettingsValues] = value;
  }
  return out;
}

/** Saklı değer aralıktaysa onu, değilse varsayılanı verir (eski satır ya da bozuk değer). */
export function raSettingOrDefault(key: keyof RaSettingsValues, value: unknown): number {
  const limit = RA_SETTING_LIMITS[key];
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= limit.min &&
    value <= limit.max
    ? value
    : RA_SETTINGS_DEFAULTS[key];
}
